/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useRef, useState} from "react"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import dayGridPlugin from "@fullcalendar/daygrid"
import {useTranslation} from "react-i18next"
import moment from "moment"
import "moment/min/locales"
import {BaseLoading, Icon} from "components"
import {CalendarProps} from "types/calendar"
import {CalendarPicker, ViewOptions, EventContent, MoreLinkContent, ConnectedCalendars, EventCard} from "./parts"
import styles from "./Calendar.module.css"
import {get, head, isEmpty, map, uniq} from "lodash"
import {useModel} from "hooks"
import {Auth} from "types/auth"
import {useQuery} from "@tanstack/react-query"
import {calendarService, eventTypeService} from "services"
import cx from "classnames"
import {Checkbox} from "antd"
import CalendarEventItem from "sections/shared/student/CalendarEventsTable/CalendarEventItem"
import {handleError} from "helpers"
import {Settings} from "types/settings"
import {CalendarViewType, DEFAULT_CALENDAR_VIEW, isDayView} from "./calendarViews"

const UPCOMING_DAYS = 60

type PanelEventsRangeMode = "weekly" | "monthly"

const getWeekRange = (day: Date, startWeekOn: number) => {
    const current = moment(day)
    const diff = (current.day() - startWeekOn + 7) % 7
    const start = current.clone().subtract(diff, "days").startOf("day")
    const end = start.clone().add(6, "days").endOf("day")
    return {start, end}
}

const getMonthRange = (day: Date) => ({
    start: moment(day).startOf("month"),
    end: moment(day).endOf("month")
})

const getUpcoming60DaysRange = () => ({
    start: moment().startOf("day"),
    end: moment().add(UPCOMING_DAYS, "days").endOf("day")
})

const formatPanelDateRange = (start: moment.Moment, end: moment.Moment) => {
    if (start.isSame(end, "year") && start.isSame(end, "month")) {
        return `${start.format("MMM D")} – ${end.format("D, YYYY")}`
    }
    if (start.isSame(end, "year")) {
        return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`
    }
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
}

const eventOverlapsRange = (
    event: {start: string; end: string},
    rangeStart: moment.Moment,
    rangeEnd: moment.Moment
) => {
    const eventStart = moment(event.start)
    const eventEnd = moment(event.end)
    return eventStart.isBefore(rangeEnd) && eventEnd.isAfter(rangeStart)
}

export const Calendar = (props: CalendarProps) => {
    const {t} = useTranslation(["calendar", "common"])
    const [allEvents, setAllEvents] = useState([])
    const [clickInfo, setClickInfo] = useState<any>({})
    const [isShowMoreEvent, setIsShowMoreEvent] = useState(false)
    const [isShowEventPreview, setIsShowEventPreview] = useState(null)
    const [moreLinkData, setMoreLinkData] = useState(null)
    const [updateViewTo, setUpdateViewTo] = useState(DEFAULT_CALENDAR_VIEW)
    const [previousViewBeforeDay, setPreviousViewBeforeDay] = useState<CalendarViewType | null>(null)
    const skipClearPreviousViewRef = useRef(false)
    const [showUpcoming60Days, setShowUpcoming60Days] = useState(false)
    const [panelEventsRangeMode, setPanelEventsRangeMode] = useState<PanelEventsRangeMode>("weekly")
    const model = useModel()
    const startWeekOn = model.getStartWeekOn()
    const {
        calendarRef,
        events,
        isLoading,
        onDateClick,
        onSelectedDayChange,
        selectedDay: selectedDayFromProps,
        onChangeDatesSet,
        eventOptions,
        viewOptions,
        isShowGoing,
        workDays,
        holidayDays,
        displayMode = "normal",
        renderWorkWeek,
        isWorkScheduleView = false,
        showConnectedCalendars,
        linkedCalendars,
        staticOptions,
        departmentId,
        updateLinkedCalendars,
        updateStaticOptions,
        viewMode = "calendar",
        setViewMode,
        ...rest
    } = props
    const [selectedDay, setSelectedDay] = useState(() => selectedDayFromProps ?? new Date())
    const isFullDisplay = displayMode === "normal"

    useEffect(() => {
        if (selectedDayFromProps) {
            setSelectedDay(selectedDayFromProps)
        }
    }, [selectedDayFromProps])
    const eventTypeIds = uniq(map(allEvents, (ev) => ev.json_data?.eventTypeId)).filter(Boolean)

    const {data: outcomeEventTypes} = useQuery({
        queryKey: ["outcomeEventTypes", eventTypeIds],
        queryFn: () => eventTypeService.getEventTypes({filter: {id: eventTypeIds}, linkedEntities: ["outcomeGroups"]}),
        enabled: eventTypeIds.length > 0
    })

    useEffect(() => {
        if (calendarRef.current) {
            calendarRef.current.getApi().gotoDate(selectedDay)
        }
    }, [selectedDay])

    useEffect(() => {
        setAllEvents(!departmentId ? events : events.filter((event) => event.json_data.departmentId === departmentId))
    }, [events, departmentId])

    const panelDateRange = useMemo(() => {
        if (panelEventsRangeMode === "monthly") {
            return getMonthRange(selectedDay)
        }
        if (showUpcoming60Days) {
            return getUpcoming60DaysRange()
        }
        return getWeekRange(selectedDay, startWeekOn)
    }, [selectedDay, showUpcoming60Days, panelEventsRangeMode, startWeekOn])

    const panelEvents = useMemo(() => {
        if (isFullDisplay) {
            return allEvents
        }

        const {start, end} = panelDateRange
        const now = moment()

        return allEvents
            .filter((event) => {
                if (!eventOverlapsRange(event, start, end)) {
                    return false
                }

                if (showUpcoming60Days) {
                    return moment(event.end).isAfter(now)
                }

                return true
            })
            .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
    }, [allEvents, isFullDisplay, panelDateRange, showUpcoming60Days])

    useEffect(() => {
        if (isFullDisplay) {
            return
        }

        const range = panelDateRange
        onChangeDatesSet({
            startStr: range.start.format(),
            endStr: range.end.format()
        })
    }, [isFullDisplay, panelDateRange])

    const onChangeView = (viewType: CalendarViewType) => {
        if (!skipClearPreviousViewRef.current) {
            setPreviousViewBeforeDay(null)
        }
        skipClearPreviousViewRef.current = false
        setUpdateViewTo(viewType)
        calendarRef.current.getApi().changeView(viewType)
    }

    const changeToDayView = (date) => {
        setUpdateViewTo(CalendarViewType.Day)
        calendarRef.current.getApi().changeView(CalendarViewType.Day)
        calendarRef.current.getApi().gotoDate(date)
        setSelectedDay(date)
    }

    const onClickToday = () => {
        const today = new Date()
        calendarRef.current.getApi().gotoDate(today)
        setSelectedDay(today)
    }

    const onClickPrevMonth = () => {
        switch (updateViewTo) {
            case CalendarViewType.Day: {
                const prevDay = moment(selectedDay).subtract(1, "day").toDate()
                setSelectedDay(prevDay)
                break
            }
            case CalendarViewType.Week: {
                const prevWeek = moment(selectedDay).subtract(1, "week").toDate()
                setSelectedDay(prevWeek)
                break
            }
            case CalendarViewType.Month: {
                const prevMonth = moment(selectedDay).subtract(1, "month").toDate()
                setSelectedDay(prevMonth)
                break
            }
            default:
                break
        }
    }

    const onClickNextMonth = () => {
        switch (updateViewTo) {
            case CalendarViewType.Day: {
                const prevDay = moment(selectedDay).add(1, "day").toDate()
                setSelectedDay(prevDay)
                break
            }
            case CalendarViewType.Week: {
                const prevWeek = moment(selectedDay).add(1, "week").toDate()
                setSelectedDay(prevWeek)
                break
            }
            case CalendarViewType.Month: {
                const prevMonth = moment(selectedDay).add(1, "month").toDate()
                setSelectedDay(prevMonth)
                break
            }
            default:
                break
        }
    }

    const onDayClick = (date) => {
        const isAlreadySelected = moment(date).isSame(selectedDay, "day")

        if (isFullDisplay && isAlreadySelected) {
            if (isDayView(updateViewTo)) {
                if (previousViewBeforeDay) {
                    onChangeView(previousViewBeforeDay)
                }
                return
            }

            setPreviousViewBeforeDay(updateViewTo)
            skipClearPreviousViewRef.current = true
            setUpdateViewTo(CalendarViewType.Day)
            calendarRef.current?.getApi().changeView(CalendarViewType.Day)
            calendarRef.current?.getApi().gotoDate(date)
            return
        }

        setSelectedDay(date)
        onSelectedDayChange?.(date)
    }

    const handleDateClick = (date) => {
        if (isShowEventPreview || isShowMoreEvent) {
            return
        }
        onDateClick(date)
    }

    const handleEventClick = (clickInfo) => {
        setClickInfo(clickInfo)
    }

    const handleShowEventPreview = (visible) => {
        setIsShowEventPreview(visible)
    }

    const handleShowMoreEventPreview = (visible) => {
        setIsShowMoreEvent(visible)
    }

    const renderEventContent = (eventInfo) => {
        return (
            <EventContent
                updateViewTo={updateViewTo}
                eventInfo={eventInfo}
                clickInfo={clickInfo}
                eventOptions={eventOptions}
                isShowGoing={isShowGoing}
                linkedCalendars={linkedCalendars}
                handleShowEventPreview={handleShowEventPreview}
                isWorkScheduleView={isWorkScheduleView}
            />
        )
    }

    const handleMoreLinkClick = (info) => {
        setIsShowMoreEvent(true)
        setMoreLinkData(info)
        setClickInfo(info)
    }

    const renderMoreLinkContent = ({text}) => {
        return (
            <MoreLinkContent
                text={text}
                moreLinkData={moreLinkData}
                updateViewTo={updateViewTo}
                clickInfo={clickInfo}
                eventOptions={eventOptions}
                handleShowEventPreview={handleShowEventPreview}
                handleShowMoreEventPreview={handleShowMoreEventPreview}
                onClickDateDetail={onClickDateDetail}
                isWorkScheduleView={isWorkScheduleView}
                isShowGoing={isShowGoing}
            />
        )
    }

    const onClickDateDetail = (date) => {
        setUpdateViewTo(CalendarViewType.Day)
        calendarRef.current?.getApi().changeView(CalendarViewType.Day)
        setSelectedDay(date)
    }

    const handleUpdateOutcome = async (event, outcomeId) => {
        const objectId = get(event, "id")
        if (!objectId || !outcomeId) return
        try {
            await calendarService.updateObjectOutcome({object_id: +objectId, outcome_id: outcomeId})
        } catch (error) {
            handleError(error)
        }
    }

    const getOutcomes = (event) => {
        const {eventTypeId} = event.json_data || {}
        const outcomeGroup: Settings.OutcomeGroup = head(
            outcomeEventTypes?.data?.find((outcome) => outcome.eventTypeId === eventTypeId)?.outcomeGroups
        )
        return outcomeGroup?.outcomes || []
    }

    const findOutcome = (event) => {
        const {completedOutcomeId} = event.json_data || {}
        if (!completedOutcomeId) return null
        const outcomes = getOutcomes(event)
        return outcomes.find((outcome) => outcome.outcomeId === completedOutcomeId)
    }

    const renderFullCalender = () => {
        return (
            <FullCalendar
                events={allEvents}
                initialView={DEFAULT_CALENDAR_VIEW}
                eventOverlap={false}
                editable={false}
                selectable={false}
                selectMirror
                allDaySlot
                dayMaxEvents={2}
                dayMaxEventRows={2}
                slotDuration="01:00:00"
                scrollTime="08:00:00"
                firstDay={startWeekOn}
                contentHeight={renderWorkWeek ? 880 : 600}
                eventOrder="order"
                nowIndicator
                navLinks
                navLinkDayClick={changeToDayView}
                // TODO locale={model.getFullCalendarLocale()}
                datesSet={isFullDisplay ? onChangeDatesSet : undefined}
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
                customButtons={{
                    todayBtn: {
                        text: "Today",
                        click: onClickToday
                    },
                    prevIcon: {
                        icon: "chevron-left",
                        click: onClickPrevMonth
                    },
                    nextIcon: {
                        icon: "chevron-right",
                        click: onClickNextMonth
                    },
                    // Non-interactive inline spinner shown next to the title while the
                    // calendar refetches in the background (e.g. realtime updates), instead
                    // of covering the whole view with a blocking overlay. Styled via CSS.
                    calendarLoading: {
                        text: " "
                    }
                }}
                headerToolbar={{
                    start: "todayBtn prevIcon,nextIcon",
                    center: isLoading && !isEmpty(events) ? "title calendarLoading" : "title",
                    end: ""
                }}
                dayCellClassNames={({date}) => {
                    const dayOfWeek = moment(date).day()
                    if (!isEmpty(holidayDays)) {
                        const isHolidayDay = holidayDays.some((holidayDay) =>
                            moment(date).startOf("date").isSame(moment(holidayDay).startOf("date"))
                        )
                        if (isHolidayDay) {
                            return "holiday-day"
                        }
                    }
                    return ""
                }}
                dayHeaderFormat={({date, localeCodes}) => {
                    const newDate = moment(date.array).locale(localeCodes[0])
                    if (updateViewTo === CalendarViewType.Month) {
                        return `${moment(newDate).format("ddd")}`
                    }
                    return `${moment(newDate).format("ddd")} \n ${moment(newDate).format("D")}`
                }}
                views={{
                    timeGridWeek: {
                        titleFormat: {year: "numeric", month: "long"}
                    }
                }}
                eventTimeFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false
                }}
                slotLabelFormat={{
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: model.user?.timeFormat === Auth.TimeFormat.Time_12
                }}
                scrollTimeReset={false}
                dateClick={handleDateClick}
                eventContent={renderEventContent}
                eventClick={handleEventClick}
                moreLinkContent={renderMoreLinkContent}
                moreLinkClick={handleMoreLinkClick}
                {...rest}
            />
        )
    }

    const renderCalendarEventsTable = () => {
        if (isEmpty(allEvents)) {
            return <div className={styles.noEventFound}>No Event Found</div>
        }
        return (
            <div className={styles.calendarEventsTable}>
                {allEvents.map((event) => (
                    <CalendarEventItem
                        key={event.id}
                        event={event}
                        isCalendarEvent
                        outcomes={getOutcomes(event)}
                        outcomeValue={findOutcome(event)}
                        handleUpdateOutcome={handleUpdateOutcome}
                    />
                ))}
            </div>
        )
    }

    // Display in Panel
    if (!isFullDisplay) {
        const panelDateRangeLabel = formatPanelDateRange(panelDateRange.start, panelDateRange.end)

        return (
            <div className={styles.panelWrap}>
                <CalendarPicker selectedDay={selectedDay} onDayClick={onDayClick} displayMode={displayMode} />
                <div className={styles.hideCalendar}>{renderFullCalender()}</div>
                <div className={styles.panelEventsHeader}>
                    <div className={styles.panelRangeModeSwitcher}>
                        <div
                            className={cx(styles.panelRangeModeSwitcherItem, {
                                [styles.panelRangeModeSwitcherItemActive]: panelEventsRangeMode === "weekly"
                            })}
                            onClick={() => setPanelEventsRangeMode("weekly")}>
                            {t("calendar.options.Week")}
                        </div>
                        <div
                            className={cx(styles.panelRangeModeSwitcherItem, {
                                [styles.panelRangeModeSwitcherItemActive]: panelEventsRangeMode === "monthly"
                            })}
                            onClick={() => {
                                setPanelEventsRangeMode("monthly")
                                setShowUpcoming60Days(false)
                            }}>
                            {t("calendar.options.Month")}
                        </div>
                    </div>
                    <div className={styles.panelDateRange}>
                        {showUpcoming60Days
                            ? t("calendar.panelEventsUpcomingRange", {range: panelDateRangeLabel})
                            : panelEventsRangeMode === "monthly"
                            ? t("calendar.panelEventsMonthRange", {range: panelDateRangeLabel})
                            : t("calendar.panelEventsWeekRange", {range: panelDateRangeLabel})}
                    </div>
                    {panelEventsRangeMode === "weekly" && (
                        <Checkbox
                            checked={showUpcoming60Days}
                            onChange={(e) => setShowUpcoming60Days(e.target.checked)}>
                            {t("calendar.showUpcoming60Days")}
                        </Checkbox>
                    )}
                </div>
                <BaseLoading isShow={isLoading && isEmpty(events)} />
                {panelEvents.length > 0 ? (
                    panelEvents.map((item) => (
                        <EventCard
                            key={item.id}
                            eventInfo={item}
                            eventOptions={eventOptions}
                            outcomeEventTypes={outcomeEventTypes}
                        />
                    ))
                ) : (
                    <div className={styles.noEventFound}>{t("calendar.noEventFound")}</div>
                )}
            </div>
        )
    }

    return (
        <div className={styles.root}>
            <div className={styles.leftSide}>
                <div className={styles.viewOptionsWrap}>
                    <ViewOptions viewOptions={viewOptions} t={t} onChange={onChangeView} updateViewTo={updateViewTo} />
                </div>
                <CalendarPicker selectedDay={selectedDay} onDayClick={onDayClick} />
                {showConnectedCalendars && (
                    <ConnectedCalendars
                        linkedCalendars={linkedCalendars}
                        staticOptions={staticOptions}
                        updateLinkedCalendars={updateLinkedCalendars}
                        updateStaticOptions={updateStaticOptions}
                    />
                )}
                {renderWorkWeek?.()}
            </div>
            <div className={styles.rightSide}>
                <BaseLoading isShow={isLoading && isEmpty(events)} />
                <div className={styles.viewModeSwitcher}>
                    <div
                        className={cx(styles.viewModeSwitcherItem, {
                            [styles.viewModeSwitcherItemActive]: viewMode === "calendar"
                        })}
                        onClick={() => setViewMode?.("calendar")}>
                        <Icon icon="CALENDAR" className={styles.viewModeSwitcherItemIcon} />
                    </div>
                    <div
                        className={cx(styles.viewModeSwitcherItem, {
                            [styles.viewModeSwitcherItemActive]: viewMode === "list"
                        })}
                        onClick={() => setViewMode?.("list")}>
                        <Icon icon="VIEW_LIST" className={styles.viewModeSwitcherItemIcon} />
                    </div>
                </div>
                <div className={cx({[styles.calendarHidden]: viewMode === "list"})}>{renderFullCalender()}</div>
                {viewMode === "list" && renderCalendarEventsTable()}
            </div>
        </div>
    )
}
